import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { deriveCapabilities, resolvePostAuthRoute } from '@/lib/tenantCapabilities';

const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'https://api.neogtm.com'
  : 'https://api.neogtm.com';
const BLOCKED_ACCESS_MESSAGE = 'Access has been temporarily blocked by the administrator.';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('Completing authentication...');

  useEffect(() => {
    const run = async () => {
    const provider = searchParams.get('provider') || 'gmail';

    // Platform auth uses full-page redirect; AuthCallback loads in same tab. isPopup is only true
    // if opened as popup (e.g. legacy flow). Wrap in try-catch to avoid Cross-Origin-Opener-Policy errors.
    let isPopup = false;
    try {
      isPopup = !!(window.opener && !window.opener.closed);
    } catch {
      isPopup = false;
    }

    const postToOpenerAndClose = (payload) => {
      if (isPopup) {
        try {
          if (window.opener && !window.opener.closed) {
            window.opener.postMessage(payload, window.location.origin);
          }
          window.close();
        } catch {
          window.close();
        }
      }
    };

    try {
      const code = searchParams.get('code');
      const error = searchParams.get('error');

      if (error) {
        setStatus('error');
        setMessage(`Authentication failed: ${error}`);
        postToOpenerAndClose({
          type: 'oauth:complete',
          provider,
          success: false,
          error: error,
        });
        if (!isPopup) setTimeout(() => navigate('/Auth'), 3000);
        return;
      }

      if (!code) {
        setStatus('error');
        setMessage('No authorization code received');
        postToOpenerAndClose({
          type: 'oauth:complete',
          provider,
          success: false,
          error: 'No authorization code received',
        });
        if (!isPopup) setTimeout(() => navigate('/Auth'), 3000);
        return;
      }

      // Check if this is an auth flow (not email connection)
      const isAuthFlow = provider === 'gmail' 
        ? localStorage.getItem('gmail_oauth_auth_flow') === 'true'
        : localStorage.getItem('outlook_oauth_auth_flow') === 'true';

      if (!isAuthFlow) {
        setStatus('error');
        setMessage('Invalid authentication flow');
        postToOpenerAndClose({
          type: 'oauth:complete',
          provider,
          success: false,
          error: 'Invalid authentication flow',
        });
        if (!isPopup) setTimeout(() => navigate('/Auth'), 3000);
        return;
      }

      // Use the existing OAuth callback endpoint, but with auth_flow flag
      // The backend should detect this and handle authentication instead of email connection
      const endpoint = provider === 'gmail'
        ? '/api/gmail/oauth/callback'
        : '/api/outlook/oauth/callback';

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          code,
          user_id: provider === 'gmail' 
            ? localStorage.getItem('gmail_oauth_user_id')
            : localStorage.getItem('outlook_oauth_user_id'),
          auth_flow: true, // Flag to indicate this is for authentication
        }),
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error(BLOCKED_ACCESS_MESSAGE);
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Authentication failed (${response.status})`);
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      // Canonical post-auth source of truth is /api/auth/session (it re-resolves/heals tenant).
      const sessionResponse = await fetch(`${API_BASE_URL}/api/auth/session`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!sessionResponse.ok) {
        throw new Error('Authentication succeeded but session verification failed');
      }
      const sessionData = await sessionResponse.json();
      if (!sessionData?.user) {
        throw new Error('No authenticated user in session response');
      }
      if (sessionData.user.id) localStorage.setItem('user-id', sessionData.user.id);
      if (sessionData.user.role) localStorage.setItem('user-role', sessionData.user.role);
      if (sessionData.user.tenant_id) {
        localStorage.setItem('user-tenant-id', sessionData.user.tenant_id);
        window.__NEO_SESSION_TENANT_ID = sessionData.user.tenant_id;
      }

      // Clean up localStorage
      if (provider === 'gmail') {
        localStorage.removeItem('gmail_oauth_user_id');
        localStorage.removeItem('gmail_oauth_auth_flow');
      } else {
        localStorage.removeItem('outlook_oauth_user_id');
        localStorage.removeItem('outlook_oauth_auth_flow');
      }

      setStatus('success');
      setMessage('Authentication successful. Redirecting...');

      if (isPopup) {
        try {
          if (window.opener && !window.opener.closed) {
            window.opener.postMessage(
              { type: 'oauth:complete', provider, success: true, email: data.user_email || data.email || null },
              window.location.origin
            );
          }
          window.close();
        } catch {
          window.close();
        }
      } else {
        /* ── came from the MCP connector? go back and finish the install ───────────────────
         * Google and Microsoft sign-in bounce through here, so a `next` handed to /Auth has to
         * survive the provider round trip or those two users land on Home with the connector
         * install abandoned half-way — and nothing tells them it failed.
         *
         * 🔴 RE-VALIDATED HERE, not trusted because /Auth checked it. This URL is reachable
         * directly, so the guard has to sit wherever the redirect is performed. Same rule: https,
         * the API origin, and the authorize path. */
        const rawNext = searchParams.get('next');
        let safeNext = null;
        if (rawNext) {
          try {
            const u = new URL(rawNext);
            if (u.protocol === 'https:' && u.origin === new URL(API_BASE_URL).origin
                && u.pathname === '/mcp/oauth/authorize') safeNext = u.toString();
            else console.warn('[AuthCallback] refusing an off-site next=', rawNext);
          } catch { /* unparseable — fall through to the normal route */ }
        }
        if (safeNext) { window.location.href = safeNext; return; }
        const postAuthRoute = resolvePostAuthRoute(deriveCapabilities(sessionData.tenant));
        setTimeout(() => navigate(postAuthRoute), 1500);
      }

    } catch (err) {
      console.error('Auth callback error:', err);
      const isBlockedError = (err?.message || '').includes(BLOCKED_ACCESS_MESSAGE);
      setStatus('error');
      setMessage(isBlockedError ? BLOCKED_ACCESS_MESSAGE : (err.message || 'Authentication failed'));

      if (isPopup) {
        try {
          if (window.opener && !window.opener.closed) {
            window.opener.postMessage(
              { type: 'oauth:complete', provider, success: false, error: err.message || 'Authentication failed' },
              window.location.origin
            );
          }
          window.close();
        } catch {
          window.close();
        }
      } else {
        localStorage.removeItem('gmail_oauth_user_id');
        localStorage.removeItem('gmail_oauth_auth_flow');
        localStorage.removeItem('outlook_oauth_user_id');
        localStorage.removeItem('outlook_oauth_auth_flow');
        setTimeout(() => navigate(isBlockedError ? '/Auth?blocked=platform' : '/Auth'), 3000);
      }
    }
    };
    run();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center p-4">
      <div className="text-center space-y-4">
        {status === 'loading' && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-[#00C2C2] mx-auto" />
            <p className="text-lg text-[#5C6B7A]">{message}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto" />
            <p className="text-lg text-green-600">{message}</p>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle className="w-12 h-12 text-red-500 mx-auto" />
            <p className="text-lg text-red-600">{message}</p>
            <p className="text-sm text-[#5C6B7A]">Redirecting to login...</p>
          </>
        )}
      </div>
    </div>
  );
}
